import { MapPinIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, { useState, useEffect, useRef, FC } from "react";
import { useLoadScript } from "@react-google-maps/api";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import Skeleton from "react-loading-skeleton";
import { components } from "react-select";

interface Props {
  onClick?: () => void;
  onChange?: (value: string) => void;
  className?: string;
  defaultValue?: string;
  headingText?: string;
  address?: string;
  userLocation?: {
    latitude: number;
    longitude: number;
    locationData: any;
  } | null;
  setUserLocation?: (location: {
    latitude: number;
    longitude: number;
    locationData: any;
  }) => void;
  selectedLocation?: any;
  setSelectedLocation?: (value: any) => void;
  error?: string;
  setError?: (value: string) => void;
}

const CustomOption = (props: any) => {
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-3 text-neutral-700 dark:text-neutral-300">
        <div className="flex-shrink-0 pt-1 pb-1">
          <MapPinIcon className="w-5 h-5 lg:w-6 lg:h-6" />
        </div>
        <div className="flex flex-col leading-snug">
          <span className="break-words">{props?.data?.label}</span>
        </div>
      </div>
    </components.Option>
  );
};

const LocationInput: FC<Props> = ({
  onChange = () => { },
  className = "",
  defaultValue = "United States",
  headingText = "Where to?",
  userLocation,
  setUserLocation,
  selectedLocation,
  setSelectedLocation,
  error,
  setError,
  address,
}) => {
  const [value, setValue] = useState("");
  const containerRef = useRef(null);
  const [googleAddress, setGoogleAddress] = useState(address ? address : "");

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY || "",
    libraries: ["places"],
  });

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const handleSelectLocation = (item: string) => {
    // DO NOT REMOVE SETTIMEOUT FUNC
    setTimeout(() => {
      setValue(item);
      onChange && onChange(item);
    }, 0);
  };

  const renderSearchValues = ({
    heading,
    items,
  }: {
    heading: string;
    items: string[];
  }) => {
    return (
      <>
        <p className="block font-semibold text-base">
          {heading || "Destinations"}
        </p>
        <div className="mt-3">
          {items.map((item) => {
            return (
              <div
                className="py-2 mb-1 flex items-center space-x-3 text-sm"
                onClick={() => handleSelectLocation(item)}
                key={item}
              >
                <MapPinIcon className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                <span className="">{item}</span>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const handlePlaceSelect = async (place: any) => {
    try {
      if (!place || !place.value || !place.value.place_id) return;
      setError && setError("");
      setSelectedLocation && setSelectedLocation(place);

      const placeId = place.value.place_id;
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${process.env.REACT_APP_GOOGLE_MAP_KEY}`
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const geocodeData = await response.json();
      if (geocodeData.status !== "OK") {
        throw new Error(`Google API Error: ${geocodeData.status}`);
      }

      if (setUserLocation) {
        const { lat, lng } = geocodeData.results[0].geometry.location;
        const addressComponents = geocodeData.results[0].address_components;
        const formattedAddress = geocodeData.results[0].formatted_address;

        let city = "";
        let state = "";
        let zipCode = "";
        let country = "";

        addressComponents.forEach((component: any) => {
          if (component.types.includes("locality")) {
            city = component.long_name;
          }
          if (component.types.includes("administrative_area_level_1")) {
            state = component.long_name;
          }
          if (
            component.types.includes("postal_code") ||
            component.types.includes("postal_code_prefix")
          ) {
            zipCode = component.long_name;
          }
          if (component.types.includes("country")) {
            country = component.long_name;
          }
        });
        const locationData = {
          street: formattedAddress || `${city}, ${state}, ${country}`,
          city: city || "N/A",
          state: state || "N/A",
          zipcode: zipCode || "N/A",
          country: country || "N/A",
        };
        setUserLocation({
          latitude: lat,
          longitude: lng,
          locationData: locationData,
        });
      }
    } catch (error) {
      console.error("Geocoding error:", error);
    }
  };

  return (
    <div className={`${className}`} ref={containerRef}>
      <div className="p-5">
        <span className="block font-semibold text-xl sm:text-2xl">
          {headingText}
        </span>
        <div className="relative mt-5">
          <div className="flex-grow">
            {!isLoaded ? (
              <Skeleton height={40} width="100%" className="rounded-md" />
            ) : (
              <GooglePlacesAutocomplete
                apiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY || ""}
                selectProps={{
                  placeholder: "Search for places...",
                  inputValue: googleAddress,
                  onInputChange: (newValue, { action }) => {
                    if (action !== "input-blur" && action !== "menu-close") {
                      setGoogleAddress(newValue);
                    }
                  },
                  onChange: (place) => {
                    if (place && place.value && place.value.place_id) {
                      handlePlaceSelect(place);
                    }
                  },
                  isClearable: true,
                  className: "font-semibold text-lg",
                  components: {
                    Option: CustomOption,
                  },
                  styles: {
                    control: (provided) => ({
                      ...provided,
                      border: "none",
                      boxShadow: "none",
                      background: "transparent",
                      fontSize: "16px",
                      padding: "6px 10px",
                    }),
                    input: (provided) => ({
                      ...provided,
                      color: "inherit",
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: "var(--text-muted, #888888)",
                      fontWeight: "500",
                    }),
                    menu: (provided) => ({
                      ...provided,
                      backgroundColor: "var(--bg-color, #ffffff)",
                      marginTop: "26px",
                      borderRadius: "12px",
                      boxShadow: "0 2px 7px rgba(0, 0, 0, 0.2)",
                      padding: "12px 0",
                      width: "100%",
                      zIndex: 1000,
                      ".dark &": {
                        backgroundColor:
                          "rgba(var(--c-neutral-800), var(--tw-bg-opacity, 1))",
                        color: "#ffffff",
                      },

                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? "rgba(0, 0, 0, 0.05)" : "transparent",
                      color: "inherit",
                      padding: "12px 20px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "start",
                      gap: "8px",
                      fontWeight: "500",
                      marginLeft: "-10px",
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      color: "inherit",
                    }),
                  },
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationInput;
